import { ChangeDetectorRef, Component } from '@angular/core';
import { ClassTransformerHelper } from '../../../../helpers/class.transformer.helper';
import { WorkflowType } from '../../../../models/enums/workflowtype.enum';
import { WorkflowModuleExecuter, WorkflowStatus } from '../../../../models/workflow/workflow.model';
import { WorkflowExitInfo, WorkflowModuleSettingsHelper, WorkflowRegistry } from '../../../../services/workflow.service';
import { DataCheck, FormulaWorkflowDialogContent } from '../../../workflow.dialog';
import { WorkflowFormulaCalculator } from '../../definevalue/define.value.settings';
import { LayoutHelper } from '../../../../helpers/layout.helper';

@Component({
    selector: 'scroll-to-dynamic-settings',
    templateUrl: './scroll.to.dynamic.settings.html'
})
export class ScrollToDynamicSettings extends FormulaWorkflowDialogContent {
    static ModuleID = 'scrollToDynamicModule';

    Data = new ScrollToDynamicSettingsData();
    RepeatElements = [];

    public static GetRegistry(): WorkflowRegistry {
        const reg = new WorkflowRegistry();
        reg.ID = ScrollToDynamicSettings.ModuleID;
        reg.Caption = '@@ScrollTo';
        reg.GroupID = 'layout';
        reg.Index = 55;
        reg.SettingsControl = ScrollToDynamicSettings;
        reg.SettingsTypeHelper = new ScrollToDynamicSettingsDataHelper();
        reg.Executer = ScrollToDynamicModulExecuter;
        reg.WorkflowType = WorkflowType.Client;
        return reg;
    }

    constructor(private cdRef: ChangeDetectorRef) {
        super();
        this.HasExpressions = true;
    }

    initialize(data: any) {
        super.initialize(data);
        if (data) {
            this.Data = ClassTransformerHelper.CopyObject(data, ScrollToDynamicSettingsData);
            this.onElementChanged();
        }
    }

    checkData(): DataCheck {
        const retVal = new DataCheck();
        if (this.Data.ElemName) {
            if (this.Data.ScrollToPosition) {
                if (!this.Data.X || !this.Data.Y) {
                    retVal.IsCorrect = false;
                    retVal.Error = '@@Bitte vergeben Sie X und Y Koordinaten';
                }
            } else if (this.Data.ScrollToRepeatIndex) {
                if (this.RepeatElements.some(x => !x.Formula)) {
                    retVal.IsCorrect = false;
                    retVal.Error = '@@Bitte vergeben Sie alle Indizes';
                }
            }
        } else {
            retVal.IsCorrect = false;
            retVal.Error = '@@Bitte waehlen Sie ein Element aus';
        }
        return retVal;
    }

    getResult(): any {
        this.Data.RepeatIndexList = this.Data.ScrollToRepeatIndex ? this.RepeatElements.map(x => x.Formula) : [];
        return this.Data;
    }

    GetExpressionProperties() {
        return [{
            Value: 'ElemName'
        }];
    }

    onElementChanged() {
        const repeatElements = [];
        if (this.Data) {
            if (this.Data.ElemName && this.WFEditOptions && this.WFEditOptions.Layout) {
                const path = LayoutHelper.GetElementPath(this.WFEditOptions.Layout, this.Data.ElemName);
                path.filter(x => x.ElementType == 'repeat').forEach((x, i) => {
                    repeatElements.push({
                        Name: x.Name,
                        Formula: this.Data.RepeatIndexList && i < this.Data.RepeatIndexList.length ? this.Data.RepeatIndexList[i] : ''
                    });
                });
            }
            if (repeatElements.length == 0 && this.Data.ScrollToRepeatIndex) {
                this.Data.ScrollToRepeatIndex = false;
            }
        }
        this.RepeatElements = repeatElements;
        this.cdRef.detectChanges();
    }
}

export class ScrollToDynamicSettingsData {
    ElemName: string;
    SmoothScroll: boolean = true;
    ScrollToPosition: boolean = false;
    X: string;
    Y: string;
    ScrollToRepeatIndex: boolean = false;
    RepeatIndexList: string[] = [];
}

export class ScrollToDynamicSettingsDataHelper extends WorkflowModuleSettingsHelper {

    getEmptySettingsInstance() {
        return new ScrollToDynamicSettingsData();
    }

    getAdditionalCaption(settings) {
        if (settings && settings.ElemName) {
            return settings.ElemName;
        }
        return super.getAdditionalCaption(settings);
    }

    getExitPoints(settings: any): WorkflowExitInfo[] {
        return [new WorkflowExitInfo()];
    }

    changeSettingsOnElementNameChange(settings: any, oldName: string, newName: string): boolean {
        if (settings && settings.ElemName === oldName) {
            settings.ElemName = newName;
            return true;
        }
        return false;
    }
}

export class ScrollToDynamicModulExecuter extends WorkflowModuleExecuter {
    private static traverseChildren(elem: Element, id: string): Element {
        if (elem) {
            if (elem.id == id) {
                return elem;
            }
            if (elem.children) {
                for (let i = 0; i < elem.children.length; i++) {
                    const foundChild = ScrollToDynamicModulExecuter.traverseChildren(elem.children[i], id);
                    if (foundChild) {
                        return foundChild;
                    }
                }
            }
        }
        return null;
    }

    async execute(status: WorkflowStatus): Promise<number> {
        if (status.ActualSettings) {
            const elemName = status.ActualSettings.ElemName;
            if (typeof elemName === 'string') {
                if (status.ActualSettings.ScrollToRepeatIndex && !status.ActualSettings.ScrollToPosition) {
                    if (status.ActualSettings.RepeatIndexList && status.ActualSettings.RepeatIndexList.length > 0) {
                        const path = status.WorkflowLayoutService.getElementPathByName(elemName);
                        if (path.length > 0) {
                            const repeats = path.filter(x => x.ElementType == 'repeat');
                            if (repeats.length > 0) {
                                const indices: number[] = [];
                                const calculator = new WorkflowFormulaCalculator(status);
                                status.ActualSettings.RepeatIndexList.forEach((x: string) => {
                                    try {
                                        const index = calculator.CalcFormula(x);
                                        if (typeof index === 'number') {
                                            indices.push(index - 1);
                                        } else {
                                            status.Logger.logError('ScrollToDynamic module: Formula \'' +
                                                status.ActualSettings.RepeatIndex + '\' does not calculate a number');
                                        }
                                    } catch (ex) {
                                        status.Logger.logError('ScrollToDynamic modul: Error while calculating formula ' +
                                            status.ActualSettings.X + ' (' + ex + ').');
                                    }
                                });
                                if (indices.length == status.ActualSettings.RepeatIndexList.length) {
                                    const elem = path[path.length - 1];
                                    let htmlelem: Element = document.getElementById('LE_RepeatChild_' + indices[0] + '_' + repeats[0].ID);
                                    let index = 1;
                                    while (htmlelem && index < indices.length && index < repeats.length) {
                                        const childID = 'LE_RepeatChild_' + indices[index] + '_' + repeats[index].ID;
                                        htmlelem = ScrollToDynamicModulExecuter.traverseChildren(htmlelem, childID);
                                        index++;
                                    }
                                    htmlelem = ScrollToDynamicModulExecuter.traverseChildren(htmlelem, 'LE_' + elem.ID);
                                    if (htmlelem) {
                                        htmlelem.scrollIntoView(status.ActualSettings.SmoothScroll ?
                                            { behavior: "smooth", block: "end", inline: "nearest" } : null);
                                        return 0;
                                    } else {
                                        status.Logger.logError('ScrollToDynamic modul: HTML Element not found.');
                                    }
                                }
                            } else {
                                status.Logger.logError('ScrollToDynamic modul: No repeat parent found.');
                            }
                        } else {
                            status.Logger.logError('ScrollToDynamic modul: Element not found.');
                        }
                    } else {
                        status.Logger.logError('ScrollToDynamic modul: No repeat index found.');
                    }
                } else {
                    const elem = status.WorkflowLayoutService.getElementByName(elemName);
                    if (elem) {
                        const htmlelem = document.getElementById('LE_Scroll_' + elem.ID) ?? document.getElementById('LE_' + elem.ID);
                        if (htmlelem) {
                            if (status.ActualSettings.ScrollToPosition) {
                                let x = 0;
                                let y = 0;
                                const calculator = new WorkflowFormulaCalculator(status);
                                if (typeof status.ActualSettings.X === 'string') {
                                    try {
                                        x = calculator.CalcFormula(status.ActualSettings.X);
                                    } catch (ex) {
                                        status.Logger.logWarning('ScrollToDynamic modul: Error while calculating formula ' +
                                            status.ActualSettings.X + ' (' + ex + ').');
                                    }
                                }
                                if (typeof status.ActualSettings.Y === 'string') {
                                    try {
                                        y = calculator.CalcFormula(status.ActualSettings.Y);
                                    } catch (ex) {
                                        status.Logger.logWarning('ScrollToDynamic modul: Error while calculating formula ' +
                                            status.ActualSettings.Y + ' (' + ex + ').');
                                    }
                                }

                                htmlelem.scrollTo({
                                    left: x,
                                    top: y,
                                    behavior: status.ActualSettings.SmoothScroll ? "smooth" : "auto"
                                });
                            } else {
                                htmlelem.scrollIntoView(status.ActualSettings.SmoothScroll ?
                                    { behavior: "smooth", block: "end", inline: "nearest" } : null);
                            }
                            return 0;
                        } else {
                            status.Logger.logError('ScrollToDynamic modul: HTML Element not found.');
                        }
                    } else {
                        status.Logger.logError('ScrollToDynamic modul: Element not found.');
                    }
                }
            } else {
                status.Logger.logError('ScrollToDynamic modul: No element name found.');
            }
        } else {
            status.Logger.logError('ScrollToDynamic modul: No settings found.');
        }
        return super.execute(status);
    }
}
